import { z } from 'zod';

import type {
  CreateDailyIntakeSnapshotRequest,
  CreateDailyIntakeSnapshotResponse,
  CreateTotalInventorySnapshotRequest,
  CreateTotalInventorySnapshotResponse,
  DailyIntakeInboundContributionDto,
  DailyIntakeManualPhysicalContributionDto,
  DailyIntakeMaterialRowDto,
  DailyIntakePurchaseContributionDto,
  DailyIntakeScopeDto,
  DailyIntakeSettlementStatus,
  DailyIntakeSnapshotDetailDto,
  DailyIntakeSnapshotRowDto,
  DailyIntakeSnapshotSummaryDto,
  DailyIntakeTotalsDto,
  DailyIntakeWarningDto,
  DailyIntakeWarningCode,
  GetDailyIntakeReviewRequest,
  GetDailyIntakeReviewResponse,
  GetReconciliationSnapshotDetailRequest,
  GetReconciliationSnapshotDetailResponse,
  GetTotalInventoryReconciliationDetailRequest,
  GetTotalInventoryReconciliationDetailResponse,
  GetTotalInventoryReconciliationRequest,
  GetTotalInventoryReconciliationResponse,
  ListReconciliationAdjustmentsRequest,
  ListReconciliationAdjustmentsResponse,
  ListReconciliationSnapshotsRequest,
  ListReconciliationSnapshotsResponse,
  ReconciliationAdjustmentTimelineItemDto,
  ReconciliationSnapshotDetailDto,
  ReconciliationSnapshotSummaryDto,
  ReconciliationWeightEventType,
  TotalInventoryBreakdownStatus,
  TotalInventoryOutboundContributionDto,
  TotalInventoryOutboundSourceType,
  TotalInventoryPhysicalUnitDetailDto,
  TotalInventoryInboundPostingContributionDto,
  TotalInventoryPurchaseContributionDto,
  TotalInventoryRowDto,
  TotalInventorySnapshotDetailDto,
  TotalInventorySnapshotRowDto,
  TotalInventorySnapshotSummaryDto,
  TotalInventoryTotalsDto,
} from './inventory-reconciliation.dto.js';
import {
  dailyIntakeSettlementStatusValues,
  dailyIntakeManualPhysicalContributionKindValues,
  dailyIntakeManualPhysicalContributionSourceEventTypeValues,
  dailyIntakeWarningCodeValues,
  reconciliationAdjustmentTypeValues,
  reconciliationSnapshotTypeValues,
  reconciliationWeightEventTypeValues,
  totalInventoryBreakdownStatusValues,
  totalInventoryOutboundSourceTypeValues,
} from './inventory-reconciliation.dto.js';

const optionalTrimmedString = z.string().trim().min(1).optional();

const nullableTrimmedString = z.string().trim().min(1).nullable();

const optionalUuid = z.uuid().optional();

export const ReconciliationWeightEventTypeSchema: z.ZodType<ReconciliationWeightEventType> = z.enum(
  reconciliationWeightEventTypeValues,
);

export const TotalInventoryBreakdownStatusSchema: z.ZodType<TotalInventoryBreakdownStatus> = z.enum(
  totalInventoryBreakdownStatusValues,
);

export const TotalInventoryOutboundSourceTypeSchema: z.ZodType<TotalInventoryOutboundSourceType> =
  z.enum(totalInventoryOutboundSourceTypeValues);

export const DailyIntakeSettlementStatusSchema: z.ZodType<DailyIntakeSettlementStatus> = z.enum(
  dailyIntakeSettlementStatusValues,
);

export const DailyIntakeWarningCodeSchema: z.ZodType<DailyIntakeWarningCode> = z.enum(
  dailyIntakeWarningCodeValues,
);

export const DailyIntakeScopeDtoSchema: z.ZodType<DailyIntakeScopeDto> = z.object({
  storeId: z.uuid().nullable(),
  locationId: z.uuid().nullable(),
  stationId: z.uuid().nullable(),
});

export const DailyIntakeTotalsDtoSchema: z.ZodType<DailyIntakeTotalsDto> = z.object({
  physicalIntakeLb: z.number(),
  systemIntakeLb: z.number(),
  varianceLb: z.number(),
  purchaseContributionCount: z.number().int().nonnegative(),
  inboundContributionCount: z.number().int().nonnegative(),
  manualPhysicalContributionCount: z.number().int().nonnegative(),
});

export const DailyIntakeMaterialRowDtoSchema: z.ZodType<DailyIntakeMaterialRowDto> = z.object({
  materialId: z.uuid().nullable(),
  physicalIntakeLb: z.number(),
  systemIntakeLb: z.number(),
  varianceLb: z.number(),
  purchaseContributionCount: z.number().int().nonnegative(),
  inboundContributionCount: z.number().int().nonnegative(),
  manualPhysicalContributionCount: z.number().int().nonnegative(),
});

export const DailyIntakePurchaseContributionDtoSchema: z.ZodType<DailyIntakePurchaseContributionDto> =
  z.object({
    purchaseCommittedId: z.uuid(),
    lineItemId: z.uuid(),
    materialId: z.uuid(),
    materialDisplayName: z.string().min(1),
    presentationType: z.enum(['SW', 'SCL', 'SCS', 'SC25', 'SP']),
    folioNumber: z.number().int().positive(),
    netWeightLb: z.number(),
    pieces: z.number().int().nullable(),
    equivalentWeightLb: z.number().nullable(),
    systemIntakeLb: z.number(),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    stationLabel: z.string().trim().min(1),
    operatorUserId: z.uuid(),
  });

export const DailyIntakeInboundContributionDtoSchema: z.ZodType<DailyIntakeInboundContributionDto> =
  z.object({
    postingId: z.uuid(),
    receiptId: z.uuid(),
    materialId: z.uuid(),
    createdHoldingUnitIds: z.array(z.uuid()),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    postedByUserId: z.uuid().nullable(),
    physicalIntakeLb: z.number(),
    systemIntakeLb: z.number(),
    officialPaymentWeightLb: z.number().optional(),
    settlementStatus: DailyIntakeSettlementStatusSchema,
  });

export const DailyIntakeManualPhysicalContributionDtoSchema: z.ZodType<DailyIntakeManualPhysicalContributionDto> =
  z.object({
    eventId: z.uuid(),
    unitId: z.uuid(),
    materialId: z.uuid().nullable(),
    contributionKind: z.enum(dailyIntakeManualPhysicalContributionKindValues).default('intake'),
    sourceEventType: z
      .enum(dailyIntakeManualPhysicalContributionSourceEventTypeValues)
      .default('holdingUnitWeightAdded'),
    reversesEventId: z.uuid().optional(),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    actorUserId: z.uuid(),
    physicalIntakeLb: z.number(),
  });

export const DailyIntakeWarningDtoSchema: z.ZodType<DailyIntakeWarningDto> = z.object({
  code: DailyIntakeWarningCodeSchema,
  message: z.string().trim().min(1),
  postingId: z.uuid().optional(),
  receiptId: z.uuid().optional(),
  materialId: z.uuid().nullable().optional(),
});

export const GetDailyIntakeReviewRequestSchema: z.ZodType<GetDailyIntakeReviewRequest> = z
  .object({
    companyId: z.uuid(),
    rangeStartAt: z.iso.datetime(),
    rangeEndAt: z.iso.datetime(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    stationId: optionalUuid,
  })
  .refine((value) => new Date(value.rangeStartAt) < new Date(value.rangeEndAt), {
    message: 'rangeStartAt must be before rangeEndAt',
    path: ['rangeEndAt'],
  });

export const GetDailyIntakeReviewResponseSchema: z.ZodType<GetDailyIntakeReviewResponse> = z.object(
  {
    generatedAt: z.iso.datetime(),
    rangeStartAt: z.iso.datetime(),
    rangeEndAt: z.iso.datetime(),
    scope: DailyIntakeScopeDtoSchema,
    totals: DailyIntakeTotalsDtoSchema,
    materials: z.array(DailyIntakeMaterialRowDtoSchema),
    purchaseContributions: z.array(DailyIntakePurchaseContributionDtoSchema),
    inboundContributions: z.array(DailyIntakeInboundContributionDtoSchema),
    manualPhysicalContributions: z.array(DailyIntakeManualPhysicalContributionDtoSchema),
    warnings: z.array(DailyIntakeWarningDtoSchema),
  },
);

export const ReconciliationSnapshotTypeSchema = z.enum(reconciliationSnapshotTypeValues);

export const DailyIntakeSnapshotRowDtoSchema = z.object({
  materialId: z.uuid().nullable(),
  physicalIntakeLb: z.number(),
  systemIntakeLb: z.number(),
  varianceLb: z.number(),
  purchaseContributionCount: z.number().int().nonnegative(),
  inboundContributionCount: z.number().int().nonnegative(),
  manualPhysicalContributionCount: z.number().int().nonnegative(),
}) satisfies z.ZodType<DailyIntakeSnapshotRowDto>;

export const TotalInventorySnapshotRowDtoSchema = z.object({
  materialId: z.uuid().nullable(),
  physicalOnHandLb: z.number(),
  systemCrvWeightLb: z.number().nullable(),
  systemScrapWeightLb: z.number().nullable(),
  systemTotalWeightLb: z.number(),
  varianceLb: z.number(),
}) satisfies z.ZodType<TotalInventorySnapshotRowDto>;

const reconciliationSnapshotBaseSchema = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  timeZone: nullableTrimmedString,
  notes: z.string().trim().max(1000).nullable(),
  createdByUserId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});

export const DailyIntakeSnapshotSummaryDtoSchema = reconciliationSnapshotBaseSchema.extend({
  type: z.literal('daily_intake'),
  generatedAt: z.iso.datetime(),
  rangeStartAt: z.iso.datetime(),
  rangeEndAt: z.iso.datetime(),
  scope: DailyIntakeScopeDtoSchema,
  physicalIntakeLb: z.number(),
  systemIntakeLb: z.number(),
  varianceLb: z.number(),
}) satisfies z.ZodType<DailyIntakeSnapshotSummaryDto>;

export const TotalInventorySnapshotSummaryDtoSchema = reconciliationSnapshotBaseSchema.extend({
  type: z.literal('total_inventory'),
  asOf: z.iso.datetime(),
  breakdownStatus: TotalInventoryBreakdownStatusSchema,
  physicalOnHandLb: z.number(),
  systemCrvWeightLb: z.number().nullable(),
  systemScrapWeightLb: z.number().nullable(),
  systemTotalWeightLb: z.number(),
  varianceLb: z.number(),
}) satisfies z.ZodType<TotalInventorySnapshotSummaryDto>;

export const ReconciliationSnapshotSummaryDtoSchema = z.discriminatedUnion('type', [
  DailyIntakeSnapshotSummaryDtoSchema,
  TotalInventorySnapshotSummaryDtoSchema,
]) satisfies z.ZodType<ReconciliationSnapshotSummaryDto>;

export const DailyIntakeSnapshotDetailDtoSchema = DailyIntakeSnapshotSummaryDtoSchema.extend({
  totals: DailyIntakeTotalsDtoSchema,
  rows: z.array(DailyIntakeSnapshotRowDtoSchema),
  purchaseContributions: z.array(DailyIntakePurchaseContributionDtoSchema),
  inboundContributions: z.array(DailyIntakeInboundContributionDtoSchema),
  manualPhysicalContributions: z.array(DailyIntakeManualPhysicalContributionDtoSchema),
  warnings: z.array(DailyIntakeWarningDtoSchema),
}) satisfies z.ZodType<DailyIntakeSnapshotDetailDto>;

export const TotalInventorySnapshotDetailDtoSchema = TotalInventorySnapshotSummaryDtoSchema.extend({
  rows: z.array(TotalInventorySnapshotRowDtoSchema),
}) satisfies z.ZodType<TotalInventorySnapshotDetailDto>;

export const ReconciliationSnapshotDetailDtoSchema = z.discriminatedUnion('type', [
  DailyIntakeSnapshotDetailDtoSchema,
  TotalInventorySnapshotDetailDtoSchema,
]) satisfies z.ZodType<ReconciliationSnapshotDetailDto>;

export const CreateDailyIntakeSnapshotRequestSchema: z.ZodType<CreateDailyIntakeSnapshotRequest> = z
  .object({
    companyId: z.uuid(),
    rangeStartAt: z.iso.datetime(),
    rangeEndAt: z.iso.datetime(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    stationId: optionalUuid,
    timeZone: optionalTrimmedString,
    notes: z.string().trim().max(1000).optional(),
  })
  .refine((value) => new Date(value.rangeStartAt) < new Date(value.rangeEndAt), {
    message: 'rangeStartAt must be before rangeEndAt',
    path: ['rangeEndAt'],
  });

export const CreateDailyIntakeSnapshotResponseSchema: z.ZodType<CreateDailyIntakeSnapshotResponse> =
  z.object({
    snapshot: DailyIntakeSnapshotDetailDtoSchema,
  });

export const CreateTotalInventorySnapshotRequestSchema: z.ZodType<CreateTotalInventorySnapshotRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    stationId: optionalUuid,
    asOf: z.iso.datetime().optional(),
    timeZone: optionalTrimmedString,
    notes: z.string().trim().max(1000).optional(),
  });

export const CreateTotalInventorySnapshotResponseSchema: z.ZodType<CreateTotalInventorySnapshotResponse> =
  z.object({
    snapshot: TotalInventorySnapshotDetailDtoSchema,
  });

export const ListReconciliationSnapshotsRequestSchema: z.ZodType<ListReconciliationSnapshotsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    type: ReconciliationSnapshotTypeSchema.optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  });

export const ListReconciliationSnapshotsResponseSchema: z.ZodType<ListReconciliationSnapshotsResponse> =
  z.object({
    items: z.array(ReconciliationSnapshotSummaryDtoSchema),
  });

export const GetReconciliationSnapshotDetailRequestSchema: z.ZodType<GetReconciliationSnapshotDetailRequest> =
  z.object({
    companyId: z.uuid(),
    snapshotId: z.uuid(),
  });

export const GetReconciliationSnapshotDetailResponseSchema: z.ZodType<GetReconciliationSnapshotDetailResponse> =
  z.object({
    snapshot: ReconciliationSnapshotDetailDtoSchema,
  });

export const TotalInventoryRowDtoSchema: z.ZodType<TotalInventoryRowDto> = z.object({
  storeId: z.uuid(),
  locationId: z.uuid(),
  materialId: z.uuid().nullable(),
  physicalOnHandLb: z.number(),
  systemCrvWeightLb: z.number(),
  systemScrapWeightLb: z.number(),
  systemTotalWeightLb: z.number(),
  inboundPostedCrvWeightLb: z.number(),
  inboundPostedScrapWeightLb: z.number(),
  inboundPostedTotalWeightLb: z.number(),
  purchasedCrvWeightLb: z.number(),
  purchasedScrapWeightLb: z.number(),
  purchasedTotalWeightLb: z.number(),
  outboundCrvWeightLb: z.number(),
  outboundScrapWeightLb: z.number(),
  outboundTotalWeightLb: z.number(),
  varianceLb: z.number(),
  physicalUnitCount: z.number().int().nonnegative(),
});

export const TotalInventoryTotalsDtoSchema: z.ZodType<TotalInventoryTotalsDto> = z.object({
  physicalOnHandLb: z.number(),
  systemCrvWeightLb: z.number(),
  systemScrapWeightLb: z.number(),
  systemTotalWeightLb: z.number(),
  inboundPostedCrvWeightLb: z.number(),
  inboundPostedScrapWeightLb: z.number(),
  inboundPostedTotalWeightLb: z.number(),
  purchasedCrvWeightLb: z.number(),
  purchasedScrapWeightLb: z.number(),
  purchasedTotalWeightLb: z.number(),
  outboundCrvWeightLb: z.number(),
  outboundScrapWeightLb: z.number(),
  outboundTotalWeightLb: z.number(),
  varianceLb: z.number(),
  physicalUnitCount: z.number().int().nonnegative(),
});

export const GetTotalInventoryReconciliationRequestSchema: z.ZodType<GetTotalInventoryReconciliationRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    materialId: z.uuid().optional(),
    asOf: z.iso.datetime().optional(),
  });

export const GetTotalInventoryReconciliationResponseSchema: z.ZodType<GetTotalInventoryReconciliationResponse> =
  z.object({
    generatedAt: z.iso.datetime(),
    asOf: z.iso.datetime(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    materialId: z.uuid().nullable(),
    totals: TotalInventoryTotalsDtoSchema,
    rows: z.array(TotalInventoryRowDtoSchema),
  });

export const TotalInventoryPhysicalUnitDetailDtoSchema: z.ZodType<TotalInventoryPhysicalUnitDetailDto> =
  z.object({
    unitId: z.uuid(),
    lifecycleStatus: z.enum(['open', 'ready']),
    materialId: z.uuid().nullable(),
    netLb: z.number(),
    yardLocationId: z.uuid().nullable(),
    updatedAt: z.iso.datetime(),
  });

export const TotalInventoryInboundPostingContributionDtoSchema: z.ZodType<TotalInventoryInboundPostingContributionDto> =
  z.object({
    postingId: z.uuid(),
    receiptId: z.uuid(),
    materialId: z.uuid(),
    createdHoldingUnitId: z.uuid(),
    isCrvEligible: z.boolean(),
    postedWeightLb: z.number(),
    crvWeightLb: z.number(),
    scrapWeightLb: z.number(),
    totalWeightLb: z.number(),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    postedByUserId: z.uuid().nullable(),
  });

export const TotalInventoryPurchaseContributionDtoSchema: z.ZodType<TotalInventoryPurchaseContributionDto> =
  z.object({
    purchaseCommittedId: z.uuid(),
    lineItemId: z.uuid(),
    materialId: z.uuid(),
    materialDisplayName: z.string().min(1),
    presentationType: z.enum(['SW', 'SCL', 'SCS', 'SC25', 'SP']),
    pieces: z.number().int().nullable(),
    equivalentWeightLb: z.number().nullable(),
    isCrvEligible: z.boolean(),
    netWeightLb: z.number(),
    crvWeightLb: z.number(),
    scrapWeightLb: z.number(),
    totalWeightLb: z.number(),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    operatorUserId: z.uuid(),
  });

export const TotalInventoryOutboundContributionDtoSchema: z.ZodType<TotalInventoryOutboundContributionDto> =
  z.object({
    sourceType: TotalInventoryOutboundSourceTypeSchema,
    sourceId: z.uuid(),
    materialId: z.uuid(),
    inventoryOutEffectiveAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    crvWeightLb: z.number(),
    scrapWeightLb: z.number(),
    totalWeightLb: z.number(),
    purchaseLineCount: z.number().int().nonnegative(),
  });

export const GetTotalInventoryReconciliationDetailRequestSchema: z.ZodType<GetTotalInventoryReconciliationDetailRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid(),
    materialId: z.uuid().nullable().optional(),
    asOf: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
  });

export const GetTotalInventoryReconciliationDetailResponseSchema: z.ZodType<GetTotalInventoryReconciliationDetailResponse> =
  z.object({
    generatedAt: z.iso.datetime(),
    asOf: z.iso.datetime(),
    locationId: z.uuid(),
    materialId: z.uuid().nullable(),
    totals: TotalInventoryRowDtoSchema,
    detailLimit: z.number().int().positive(),
    hasMorePhysicalUnits: z.boolean(),
    hasMoreInboundPostingContributions: z.boolean(),
    hasMorePurchaseContributions: z.boolean(),
    hasMoreOutboundContributions: z.boolean(),
    physicalUnits: z.array(TotalInventoryPhysicalUnitDetailDtoSchema),
    inboundPostingContributions: z.array(TotalInventoryInboundPostingContributionDtoSchema),
    purchaseContributions: z.array(TotalInventoryPurchaseContributionDtoSchema),
    outboundContributions: z.array(TotalInventoryOutboundContributionDtoSchema),
  });

export const ReconciliationAdjustmentTimelineItemDtoSchema: z.ZodType<ReconciliationAdjustmentTimelineItemDto> =
  z.object({
    id: z.uuid(),
    type: z.enum(reconciliationAdjustmentTypeValues),
    occurredAt: z.iso.datetime(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    shipmentId: z.uuid().nullable(),
    materialId: z.uuid().nullable(),
    summary: z.string().min(1),
    details: z.record(z.string(), z.unknown()).nullable(),
  });

export const ListReconciliationAdjustmentsRequestSchema: z.ZodType<ListReconciliationAdjustmentsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: optionalUuid,
    locationId: optionalUuid,
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  });

export const ListReconciliationAdjustmentsResponseSchema: z.ZodType<ListReconciliationAdjustmentsResponse> =
  z.object({
    items: z.array(ReconciliationAdjustmentTimelineItemDtoSchema),
  });
